/* eslint-disable no-new-func */
import moment from "moment"

// Database constants
const DESCRIPTION = "Description"
const NAME = "Name"
const PERIOD_START = "PeriodStart"
const REPORTING = "Reporting"
const VALUE = "Value"

// UI constants
const TOTAL = "Total"

/**
 * Maps the output and collects the years in an array of table columns, as
 * well as creates a UI data object containing the table row values and the
 * total for each row. The columns and values are then combined with the
 * template data in subsequent functions in order to generate the UI data for
 * badges and tables.
 */

export const mapOutputToTemplate = (output, template) => {
  // Initialize an object for storing the table values
  const values = {}

  // Iterate over the output periods
  const years = output.map((period) => {
    // Prepare the year value for this period
    const year = moment(period[PERIOD_START]).format("YYYY")

    // Iterate over the output rows
    period[REPORTING].forEach((row) => {
      const rowName = row[NAME]
      const rowValue = row[VALUE]

      // If this row does not exist in the values object
      if (!values[rowName]) {
        // Set the row name, cell values, and total value for this row in the values object
        return (values[rowName] = {
          name: row[DESCRIPTION],
          cells: {
            [year]: rowValue,
            [TOTAL]: rowValue,
          },
        })
      }

      // If this row exists in values, update the cell value and total value
      values[rowName].cells[year] = rowValue
      values[rowName].cells[TOTAL] += rowValue
    })

    // Return the year for the years array
    return year
  })

  // Create an array for the columns, with a column for the totals
  const columns = [
    ...years.map((year) => ({code: year, name: year})),
    {code: TOTAL, name: TOTAL},
  ]

  return {
    badges: makeBadges(values, template.badges),
    columns,
    tables: makeTables(columns, values, template.tables),
  }
}

/**
 * Maps the badges in the template and uses the values object to create a data
 * object for each badge. If the badge has a formula, then the result is
 * calculated and added to the data object.
 */

const makeBadges = (values, badges) =>
  badges.map((badge) => {
    // Initialize an object for the badge data
    const badgeData = {name: badge.name}

    // If the badge has a code
    if (badge.code) {
      // Update the name and value on the badge data object, using the Total for value
      badgeData.name = values[badge.code].name
      badgeData.value = values[badge.code].cells[TOTAL]
    }

    // If the badge has a formula
    if (badge.formula) {
      // Get the formula result and add it to the badge data object
      badgeData.value = makeFormulaResult(badge.formula, TOTAL, values)
    }

    // If the badge has a chart
    if (badge.chart) {
      // Initialize empty arrays for the chart labels and values
      const chartLabels = []
      const chartValues = []

      badge.chart.values.forEach((chartValue) => {
        // If the chart value has a formula
        if (chartValue.formula) {
          // Get the formula result and add it to the chart value data object
          chartValues.push(makeFormulaResult(chartValue.formula, TOTAL, values))
        }

        // If the chart value has a code
        if (chartValue.code) {
          // Get the value name from the values object and push it to the labels array
          chartLabels.push(values[chartValue.code].name)
          // Get the total value from ouput values and push it to the values array
          chartValues.push(values[chartValue.code].cells[TOTAL])
        }

        // If the chart value has a name, push it to the labels array
        if (chartValue.name) chartLabels.push(chartValue.name)
      })

      // Add the labels and values to the badge chart object
      badgeData.chart = {
        colors: badge.chart.colors,
        labels: chartLabels,
        values: chartValues,
      }
    }

    return badgeData
  })

/**
 * Maps the tables in the template and uses the values object to create a data
 * object for each table. If a table row or sub-row has a formula, the result
 * is calculated and added to the data object.
 */

const makeTables = (columns, values, tables) =>
  tables.map((table) => {
    // Map the rows of the template table
    const rows = table.rows.map((row) => {
      // Initialize a row data
      const rowData = {
        name: row.name,
        bold: !!row.bold,
        cells: {},
      }

      // If this row has a code
      if (row.code) {
        // Get the row name and cells from the values object
        rowData.name = values[row.code].name
        rowData.cells = values[row.code].cells
      }

      // If this row has a formula
      if (row.formula) {
        // Get the formula result and add it to the row data object
        columns.forEach(({code}) => {
          rowData.cells[code] = makeFormulaResult(row.formula, code, values)
        })
      }

      // If this row has sub-rows
      if (row.rows) {
        // Map over the sub-row
        rowData.rows = row.rows.map((child) => {
          // Get the sub-row name and cells from the values object
          return {
            name: values[child.code].name,
            cells: values[child.code].cells,
          }
        })
      }

      return rowData
    })

    return {...table, rows}
  })

/**
 * Takes a formula string, column name, and output object, and returns the
 * result of the formula for that column. It is used for table rows, badges,
 * and badge sub-items which do not have an output value, but are calculated
 * using multiple output rows.
 */

const makeFormulaResult = (formula, column, values) => {
  // Initialize an empty array for the function values
  const functionValues = []

  // Map the row keys of the values object
  const functionFields = Object.keys(values).map((key) => {
    // Find the column value for this row
    const value = values[key].cells[column]
    // Push the column value to the function values array
    functionValues.push(value)
    // Push the key to the function fields array
    return key
  })

  // Fire the function with the fields and values
  return new Function(...functionFields, `return ${formula}`)(...functionValues)
}
